/**
 * Progress toast utility for long-running operations
 * Uses sonner's custom toast API to show animated progress indicators
 */

import { toast } from 'sonner'
import { Loader2, CheckCircle2, XCircle } from 'lucide-react'

interface ProgressToastOptions {
  /** Unique ID for the toast (allows updates/dismissal) */
  id: string
  /** Main message to display */
  message: string
  /** Optional description/subtitle */
  description?: string
  /** Progress percentage (0-100). If undefined, shows indeterminate animation */
  progress?: number
}

/**
 * Show or update a progress toast
 * Call with the same ID to update an existing toast
 */
export function showProgressToast({ id, message, description, progress }: ProgressToastOptions) {
  toast.custom(
    () => (
      <div className="flex flex-col gap-2 bg-background border border-border rounded-lg p-4 shadow-lg w-[356px]">
        <div className="flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-foreground">{message}</p>
            {description && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">{description}</p>
            )}
          </div>
        </div>
        <div className="h-1.5 w-full rounded-full bg-primary/20 overflow-hidden relative">
          {progress !== undefined ? (
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${progress}%` }}
            />
          ) : (
            <>
              <div className="h-full w-full bg-primary/40" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent animate-progress-shimmer" />
            </>
          )}
        </div>
      </div>
    ),
    { id, duration: Infinity }
  )
}

/**
 * Complete a progress toast with success state
 */
export function completeProgressToast(id: string, message: string) {
  toast.custom(
    () => (
      <div className="flex items-center gap-3 bg-background border border-border rounded-lg p-4 shadow-lg w-[356px]">
        <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
        <p className="font-medium text-sm text-foreground">{message}</p>
      </div>
    ),
    { id, duration: 3000 }
  )
}

/**
 * Complete a progress toast with error state
 */
export function errorProgressToast(id: string, message: string) {
  toast.custom(
    () => (
      <div className="flex items-center gap-3 bg-background border border-destructive/50 rounded-lg p-4 shadow-lg w-[356px]">
        <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
        <p className="font-medium text-sm text-foreground">{message}</p>
      </div>
    ),
    { id, duration: 4000 }
  )
}

/**
 * Dismiss a progress toast
 */
export function dismissProgressToast(id: string) {
  toast.dismiss(id)
}

/**
 * Helper to run an async operation with progress toast
 * Handles showing, completing, and error states automatically
 */
export async function withProgressToast<T>(
  options: {
    id: string
    loadingMessage: string
    loadingDescription?: string
    successMessage: string
    errorMessage?: string
  },
  operation: () => Promise<T>
): Promise<T> {
  showProgressToast({
    id: options.id,
    message: options.loadingMessage,
    description: options.loadingDescription,
  })

  try {
    const result = await operation()
    completeProgressToast(options.id, options.successMessage)
    return result
  } catch (error) {
    const errorMsg = options.errorMessage || (error instanceof Error ? error.message : 'An error occurred')
    errorProgressToast(options.id, errorMsg)
    throw error
  }
}
